<template>
  <div class="calendar-overlay" @mousedown="onOverlayClick">
    <v-calendar
      ref="calendar"
      :masks="masks"
      :attributes="allAttributes"
      :min-date="new Date()"
      :max-date="new Date(new Date().setDate(new Date().getDate() + 365))"
      :columns="1"
      :rows="1"
      :locale="{ id: this.$route.path.split('/')[1], firstDayOfWeek: 2, masks: { weekdays: 'WW' } }"
      mode="date"
      class="custom-calendar"
    />
    />
  </div>
</template>

<script>
export default {
  name: 'CalendarPicker',
  props: {
    apartmentId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      selectedDate: null,
      isMobile: false,
      unavailableDates: [],
      isLoading: true,
    }
  },
  computed: {
    allAttributes() {
      return [
        {
          key: 'today',
          bar: {
            color: 'blue',
            style: {
              borderRadius: '20%',
              height: '5px',
            },
          },
          dates: new Date()
        },
        {
          key: 'before-today',
          highlight: {
            color: 'gray',
            style: {
              borderRadius: '25%',
              opacity: 0.2
            },
          },
          dates: Array.from({ length: 30 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - 30 + i);
            return new Date(date.getFullYear(), date.getMonth(), date.getDate());
          }),
        },
        {
          key: 'unavailable',
          highlight: {
            color: 'red',
            style: {
              borderRadius: '50%',
              opacity: 0.75
            },
          },
          dates: this.unavailableDates.filter(date => {
            const d = new Date(date);
            const today = new Date();
            today.setHours(0,0,0,0);
            d.setHours(0,0,0,0);
            return d >= today;
          }),
          popover: {
            label: this.$t('calendar.unavailable')
          },
          order: -1
        },
        this.selectedDate && !this.isDateUnavailable(this.selectedDate) && {
          key: 'selected',
          highlight: {
            color: 'blue',
            fillMode: 'solid'
          },
          dates: this.selectedDate,
          popover: {
            label: this.$t('calendar.selected')
          }
        }
      ].filter(Boolean)
    }
  },
  async created() {
    await this.fetchUnavailableDates();
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  // watch: {
  //   apartmentId: {
  //     immediate: true,
  //     handler: 'fetchUnavailableDates'
  //   }
  // },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    async fetchUnavailableDates() {
      try {
        this.isLoading = true;
        const response = await fetch(`/api/calendar/ap${this.apartmentId}`);
        // const response = await fetch(`http://localhost:8001/calendar/ap${this.apartmentId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch unavailable dates');
        }
        const data = await response.json();
        
        // Convert ISO date strings to Date objects and ensure they're valid
        this.unavailableDates = data.message
          .map(dateStr => {
            try {
              const [year, month, day] = dateStr.split('-').map(Number);
              const date = new Date(year, month - 1, day);
              // Check if date is valid
              return date.getTime() ? date : null;
            } catch (e) {
              console.error('Invalid date:', dateStr);
              return null;
            }
          })
          .filter(date => date !== null); // Remove any invalid dates
      } catch (error) {
        console.error('Error fetching unavailable dates:', error);
        this.unavailableDates = [];
      } finally {
        this.isLoading = false;
      }
    },
    onDayClick(day) {
      if (!this.isDateUnavailable(day.date)) {
        this.selectedDate = day.date;
      }
    },
    isDateUnavailable(date) {
      return this.unavailableDates.some(unavailableDate => 
        this.isSameDay(new Date(unavailableDate), new Date(date))
      );
    },
    isSameDay(date1, date2) {
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate();
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    onOverlayClick(event) {
      if (event.target === this.$el) {
        this.$emit('close');
      }
    }
  }
}
</script>

<style scoped>
.calendar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.1);
  z-index: 1000;
}

.custom-calendar {
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  padding: 1rem;
}

:deep(.vc-highlight-red) {
  background-color: rgba(255, 0, 0, 0.1) !important;
  border: 1px solid red !important;
}

:deep(.vc-day-content) {
  color: inherit !important;
}

:deep(.vc-day.is-disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .calendar-picker {
    width: 90%;
  }
}
</style>